import { client } from "./axiosClient";

export function registerProfile(body: any) {
  return client.post("auth/register", body);
}

export function login(body: any) {
  return client.post("auth/login", body);
}

export function logout(body: any) {
  return client.post("auth/logout", body);
}

export function forgotPassword(body: any) {
  return client.post("auth/send-forgot-password-email", body);
}

export function resetPassword(body: any) {
  return client.patch("auth/reset-password-using-email-token", body);
}

export function refreshAccessToken() {
  return client.post("auth/refresh-access-token", {
    refreshToken: localStorage.getItem("refreshToken"),
  });
}

export function verifyEmail(token: string) {
  return client.get(`auth/email-verification?token=${token}`);
}

export function uploadProfileImage(formData: FormData, folder?: string) {
  return client.post(
    `user/upload-profile-image${folder ? `?folder=${folder}` : ""}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
}

export function uploadCoverImage(formData: FormData, folder?: string) {
  return client.post(
    `user/upload-profile-cover-image${folder ? `?folder=${folder}` : ""}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
}

export function getProfile() {
  return client.get("user/profile");
}

export function updateProfile(data: any) {
  return client.patch("auth/update-profile", data);
}

export function updateBio(bio: string) {
  return client.post("user/upload-bio", { bio });
}

export function UploadFile(body: any) {
  return client.post("auth/upload-doc", body);
}

export function getUserProfileWithLinkedData(body: any) {
  return client.post("auth/profile-with-connected-data", body);
}

// ─── Generic CRUD ─────────────────────────────────────────────────────────────

export function insertDataInTable(tableName: string, body: any) {
  return client.post(`${tableName}/create`, body);
}

export function insertBulkDataInTable(tableName: string, body: any[]) {
  return client.post(`${tableName}/insertMany`, body);
}

export function getOneDataFromTable(tableName: string, id: any) {
  return client.get(`${tableName}/get-one-record/${id}`);
}

export function searchDataFromTable(tableName: string, query: any) {
  return client.get(`${tableName}/search-one-record`, { params: query });
}

export function searchListDataFromTable(tableName: string, body: any) {
  return client.post(`${tableName}/search-record`, body);
}

export function getDataFromTable(tableName: string, params?: any) {
  return client.get(`${tableName}/get-all-record`, { params });
}

export function getDataFromTableWithPagination(
  tableName: string,
  params?: any,
) {
  return client.get(`${tableName}/get-all-record-with-pagination`, { params });
}

export function getOpenDataFromTable(tableName: string, body: any) {
  return client.post(`${tableName}/get-all-open-record`, body);
}

export function updateByIdDataInTable(tableName: string, id: any, body: any) {
  return client.patch(`${tableName}/update-record/${id}`, body);
}

export function updateRecordById(tableName: string, id: any, body: any) {
  return client.put(`${tableName}/update-record/${id}`, body);
}

export function updateByFieldDataInTable(
  tableName: string,
  query: any,
  body: any,
) {
  return client.put(`${tableName}/update-status-by-key`, body, {
    params: query,
  });
}

export function deleteDataFromTable(tableName: string, id: any) {
  return client.delete(`${tableName}/delete-record/${id}`);
}

// ─── Course ───────────────────────────────────────────────────────────────────

export interface CreateCourseRequest {
  title: string;
  categoryId: number | string;
  timeline: string;
  createdBy?: number;
  status: string;
  teachersName?: string;
}

export function createCourse(body: CreateCourseRequest) {
  return client.post("course/create", body);
}

export interface UpdateCourseDescriptionRequest {
  learningObjectives: string[];
  whoCanJoin: string;
  requiredSkills: string;
}

export function updateCourseDescription(
  courseId: string,
  body: UpdateCourseDescriptionRequest,
) {
  return client.put(`course/update-record/${courseId}`, body);
}

// ─── Course Content ───────────────────────────────────────────────────────────

export interface CreateCourseContentRequest {
  courseId: string;
  lectureName: string;
  lectureDesc: string;
  lectureOrder: number;
  createdBy: number | string;
  status: string;
  contentType?: string;
  teachersName?: string;
}

export function createCourseContent(body: CreateCourseContentRequest) {
  return client.post("courseContent/create", body);
}

// ─── Course Landing Page ──────────────────────────────────────────────────────

export interface UpdateCourseLandingPageRequest {
  title?: string;
  subTitle?: string;
  description?: string;
  category?: string;
  level?: string;
  language?: string;
}

export function updateCourseLandingPage(
  courseId: string,
  body: UpdateCourseLandingPageRequest,
) {
  return client.put(`course/update-record/${courseId}`, body);
}

export function uploadCourseCoverImage(
  courseId: number | string,
  formData: FormData,
  folder?: string,
) {
  return client.post(
    `course/upload-cover-image/${courseId}?${folder ? `?folder=${folder}` : ""}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
}

export function uploadCourseShortVideo(
  courseId: number | string,
  formData: FormData,
  folder?: string,
) {
  return client.post(
    `course/upload-short-video/${courseId}${folder ? `?folder=${folder}` : ""}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
}

// ─── Course Price ─────────────────────────────────────────────────────────────

export interface UpdateCoursePriceRequest {
  unitOfPrice: string;
  price: string;
}

export function updateCoursePrice(
  courseId: string,
  body: UpdateCoursePriceRequest,
) {
  return client.put(`course/update-record/${courseId}`, body);
}

// ─── Publish Course ───────────────────────────────────────────────────────────

export function publishCourse(courseId: string) {
  return client.put(`course/update-record/${courseId}`, {
    status: "published",
  });
}

// ─── User Wishlist ────────────────────────────────────────────────────────────

export function getUserWishlist(userId: number | string) {
  return client.get("userWishlist/get-all-record", { params: { userId } });
}

export function addToWishlist(body: { courseId: number; userId: number }) {
  return client.post("userWishlist/create", body);
}

export function removeFromWishlist(id: number | string) {
  return client.delete(`userWishlist/delete-record/${id}`);
}

// ─── User Cart ────────────────────────────────────────────────────────────────

export function getUserCart(userId: number | string) {
  return client.get("userCart/get-all-record", { params: { userId } });
}

export function addToCart(body: {
  productId: number;
  userId: number;
  status?: string;
}) {
  return client.post("userCart/create", body);
}

export function removeFromCart(id: number | string) {
  return client.delete(`userCart/delete-record/${id}`);
}

// ─── Enrolled Course ──────────────────────────────────────────────────────────

export function getEnrolledCourses(userId: number | string) {
  return client.get("enrolledCourse/get-all-record", { params: { userId } });
}

export function enrollInCourse(body: { courseId: number; userId: number }) {
  return client.post("enrolledCourse/create", body);
}

export function updateEnrolledCourse(
  id: number | string,
  body: { courseId?: number; userId?: number },
) {
  return client.put(`enrolledCourse/update-record/${id}`, body);
}

export function deleteEnrolledCourse(id: number | string) {
  return client.delete(`enrolledCourse/delete-record/${id}`);
}

// ─── Payments ─────────────────────────────────────────────────────────────────

export function getUserPayments(userId: number | string) {
  return client.get("payments/get-all-record", { params: { userId } });
}

export function getUserPaymentsWithPagination(params: {
  userId?: number | string;
  page?: number;
  limit?: number;
  status?: string;
}) {
  return client.get("payments/get-all-record-with-pagination", { params });
}

export function createPayment(body: {
  amount?: number;
  baseAmount?: number;
  currency?: string;
  userId?: number;
  modeOfPayment?: string;
  purpose?: string;
  categoryOfPayment?: string;
  transactionId?: string;
  orderId?: string;
  courseId?: number;
  status?: string;
  vatPercentage?: number;
  checkoutUrl?: string;
  customerId?: string;
  paymentThirdPartyType?: string;
  refundDate?: string;
}) {
  return client.post("payments/create", body);
}

export function getOnePayment(id: number | string) {
  return client.get(`payments/get-one-record/${id}`);
}

// ─── Notifications ────────────────────────────────────────────────────────────

export function getUserNotifications(userId: number | string) {
  return client.get("notifications/get-all-record", { params: { userId } });
}

export function getUserNotificationsWithPagination(params: {
  userId?: number | string;
  page?: number;
  limit?: number;
  type?: string;
}) {
  return client.get("notifications/get-all-record-with-pagination", { params });
}

export function createNotification(body: {
  title: string;
  message: string;
  type?: string;
  userId: number;
}) {
  return client.post("notifications/create", body);
}

export function updateNotification(
  id: number | string,
  body: { title?: string; message?: string; type?: string; userId?: number },
) {
  return client.put(`notifications/update-record/${id}`, body);
}

export function deleteNotification(id: number | string) {
  return client.delete(`notifications/delete-record/${id}`);
}

// ─── Notification Settings ────────────────────────────────────────────────────

export function getNotificationSettings(params?: any) {
  return client.get("notificationSettings/get-all-record", { params });
}

export function createNotificationSettings(body: {
  isTrackCourseVisibility?: boolean;
  isTrackCourseCompleteness?: boolean;
  isTrackProfileCompleteness?: boolean;
  isTrackNewCourse?: boolean;
  isTrackNewLession?: boolean;
  isTrackTopicVisibility?: boolean;
  isTrackNewTopic?: boolean;
  isTrackSkillEffect?: boolean;
}) {
  return client.post("notificationSettings/create", body);
}

export function updateNotificationSettings(
  id: number | string,
  body: {
    isTrackCourseVisibility?: boolean;
    isTrackCourseCompleteness?: boolean;
    isTrackProfileCompleteness?: boolean;
    isTrackNewCourse?: boolean;
    isTrackNewLession?: boolean;
    isTrackTopicVisibility?: boolean;
    isTrackNewTopic?: boolean;
    isTrackSkillEffect?: boolean;
  },
) {
  return client.put(`notificationSettings/update-record/${id}`, body);
}

// ─── User Activity ────────────────────────────────────────────────────────────

export function getUserActivity(userId: number | string) {
  return client.get("userActivity/get-all-record", { params: { userId } });
}

export function createUserActivity(body: {
  activityAction: string;
  activityType: string;
  userId: number;
  activityRelatedId?: number;
}) {
  return client.post("userActivity/create", body);
}

// ─── User Ratings ─────────────────────────────────────────────────────────────

export function getUserRatings(userId?: number | string) {
  return client.get("userRatings/get-all-record", {
    params: userId ? { userId } : {},
  });
}

export function createUserRating(body: {
  rating: number;
  feedback?: string;
  feedbackType?: string;
  userId: number;
}) {
  return client.post("userRatings/create", body);
}

// ─── Skills ───────────────────────────────────────────────────────────────────

export function getAllSkills(params?: any) {
  return client.get("skills/get-all-record", { params });
}

export function createSkill(body: {
  title: string;
  createdBy?: number;
  status?: string;
}) {
  return client.post("skills/create", body);
}

// ─── Topics ───────────────────────────────────────────────────────────────────

export function getAllTopics(params?: any) {
  return client.get("topics/get-all-record", { params });
}

// ─── Support ──────────────────────────────────────────────────────────────────

export function createSupportTicket(body: {
  query: string;
  queryType?: string;
  queryDesc?: string;
  userId: number;
  userType?: string;
  status?: string;
}) {
  return client.post("support/create", body);
}

export function getUserSupportTickets(userId: number | string) {
  return client.get("support/get-all-record", { params: { userId } });
}

// ─── Question & Answer ────────────────────────────────────────────────────────

export interface QuestionRecord {
  id: number;
  name: string;
  courseId: number;
  contentId: number;
  questionType: "mcq" | "yes/no" | "short_answer" | "fill_in_the_blank";
  answer?: string;
  options?: string[];
  status?: string;
  createdBy?: number;
}

export function getCourseQuestions(params: {
  courseId: string | number;
  contentId?: string | number;
  status?: string;
  questionType?: string;
}) {
  return getDataFromTable("question", params);
}

export function createAnswer(body: {
  userOutcome: string;
  questionId: string | number;
  courseId: string | number;
  courseContentId?: string | number;
  userId: string | number;
  status?: string;
}) {
  return insertDataInTable("answer", body);
}

export function getUserAnswers(params: {
  userId: string | number;
  questionId?: string | number;
}) {
  return getDataFromTable("answer", params);
}

export function uploadCourseContentVideo(
  lectureId: number | string,
  formData: FormData,
  folder?: string,
) {
  return client.post(
    `courseContent/upload-lecture-video/${lectureId}${folder ? `?folder=${folder}` : ""}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
}